import React, { useEffect, useState } from "react";
import { CompanyKeyMetrics } from "../../company";
import { useOutletContext } from "react-router";
import { getKeyMetrics } from "../../api";
import RatioList from "../RatioList/RatioList";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {
  formatLargeMonetaryNumber,
  formatLargeNonMonetaryNumber,
  formatRatio,
} from "../Helpers/NumberFormatting";
import StockComment from "../StockComment/StockComment";

interface Props {}

const tableConfig = [
  {
    label: "Average Inventory",
    subTitle: "Average value of inventory held during the period.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.averageInventoryTTM || 0)),
  },
  {
    label: "Average Payables",
    subTitle: "Average accounts payable during the period.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.averagePayablesTTM || 0)),
  },
  {
    label: "Average Receivables",
    subTitle: "Average accounts receivable over the period.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.averageReceivablesTTM || 0)),
  },
  {
    label: "CapEx to Depreciation",
    subTitle: "Compares capital expenditure to depreciation expense.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.capexToDepreciationTTM || 0)),
  },
  {
    label: "CapEx to Operating Cash Flow",
    subTitle: "Portion of operating cash flow used for capital expenditure.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.capexToOperatingCashFlowTTM || 0)),
  },
  {
    label: "CapEx to Revenue",
    subTitle: "Capital expenditure as a share of revenue.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.capexToRevenueTTM || 0)),
  },
  {
    label: "Cash Conversion Cycle",
    subTitle: "Time taken to convert investments into cash flow.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeNonMonetaryNumber(Number(company.cashConversionCycleTTM || 0)),
  },
  {
    label: "Current Ratio",
    subTitle: "Measures short-term liquidity using current assets and liabilities.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.currentRatioTTM || 0)),
  },
  {
    label: "Days of Inventory Outstanding",
    subTitle: "Average days inventory is held before sale.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeNonMonetaryNumber(Number(company.daysOfInventoryOutstandingTTM || 0)),
  },
  {
    label: "Days of Payables Outstanding",
    subTitle: "Average days to pay suppliers.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeNonMonetaryNumber(Number(company.daysOfPayablesOutstandingTTM || 0)),
  },
  {
    label: "Days of Sales Outstanding",
    subTitle: "Average number of days to collect revenue from customers.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeNonMonetaryNumber(Number(company.daysOfSalesOutstandingTTM || 0)),
  },
  {
    label: "Earnings Yield",
    subTitle: "Inverse of the P/E ratio; shows earnings return per share price.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.earningsYieldTTM || 0)),
  },
  {
    label: "Enterprise Value",
    subTitle: "Total company value including debt and excluding cash.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.enterpriseValueTTM || 0)),
  },
  {
    label: "EV to EBITDA",
    subTitle: "Compares enterprise value to EBITDA.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.evToEBITDATTM || 0)),
  },
  {
    label: "EV to Free Cash Flow",
    subTitle: "Enterprise value compared to free cash flow.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.evToFreeCashFlowTTM || 0)),
  },
  {
    label: "EV to Operating Cash Flow",
    subTitle: "Enterprise value compared to operating cash flow.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.evToOperatingCashFlowTTM || 0)),
  },
  {
    label: "EV to Sales",
    subTitle: "Enterprise value relative to total revenue.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.evToSalesTTM || 0)),
  },
  {
    label: "Free Cash Flow to Equity",
    subTitle: "Cash available to shareholders after expenses and reinvestment.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.freeCashFlowToEquityTTM || 0)),
  },
  {
    label: "Free Cash Flow to Firm",
    subTitle: "Cash flow available to all investors, debt and equity.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.freeCashFlowToFirmTTM || 0)),
  },
  {
    label: "Free Cash Flow Yield",
    subTitle: "Free cash flow as a percentage of market cap.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.freeCashFlowYieldTTM || 0)),
  },
  {
    label: "Graham Net-Net",
    subTitle: "Benjamin Graham’s net current asset valuation.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.grahamNetNetTTM || 0)),
  },
  {
    label: "Graham Number",
    subTitle: "Intrinsic value estimate based on EPS and book value.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.grahamNumberTTM || 0)),
  },
  {
    label: "Income Quality",
    subTitle: "Ratio of cash flow to net income; measures earnings reliability.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.incomeQualityTTM || 0)),
  },
  {
    label: "Intangibles to Total Assets",
    subTitle: "Share of intangible assets in total assets.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.intangiblesToTotalAssetsTTM || 0)),
  },
  {
    label: "Interest Burden",
    subTitle: "Effect of interest expenses on profitability.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.interestBurdenTTM || 0)),
  },
  {
    label: "Invested Capital",
    subTitle: "Total capital invested in operations.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.investedCapitalTTM || 0)),
  },
  {
    label: "Market Cap",
    subTitle: "Total market value of all outstanding shares.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.marketCap || 0)),
  },
  {
    label: "Net Current Asset Value",
    subTitle: "Current assets minus total liabilities.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.netCurrentAssetValueTTM || 0)),
  },
  {
    label: "Net Debt to EBITDA",
    subTitle: "Debt level relative to EBITDA earnings.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.netDebtToEBITDATTM || 0)),
  },
  {
    label: "Operating Cycle",
    subTitle: "Time taken to convert inventory into cash flow.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeNonMonetaryNumber(Number(company.operatingCycleTTM || 0)),
  },
  {
    label: "Operating Return on Assets",
    subTitle: "Operating income as a percentage of total assets.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.operatingReturnOnAssetsTTM || 0)),
  },
  {
    label: "R&D to Revenue",
    subTitle: "Research and development spending as a share of revenue.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.researchAndDevelopementToRevenueTTM || 0)),
  },
  {
    label: "Return on Assets",
    subTitle: "Net income as a percentage of total assets.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.returnOnAssetsTTM || 0)),
  },
  {
    label: "Return on Capital Employed",
    subTitle: "Efficiency of using capital to generate profits.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.returnOnCapitalEmployedTTM || 0)),
  },
  {
    label: "Return on Equity",
    subTitle: "Profitability relative to shareholders’ equity.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.returnOnEquityTTM || 0)),
  },
  {
    label: "Return on Invested Capital",
    subTitle: "Profit generated relative to invested capital.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.returnOnInvestedCapitalTTM || 0)),
  },
  {
    label: "Return on Tangible Assets",
    subTitle: "Return generated from tangible assets only.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.returnOnTangibleAssetsTTM || 0)),
  },
  {
    label: "SG&A to Revenue",
    subTitle: "Administrative and selling expenses as a percentage of sales.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.salesGeneralAndAdministrativeToRevenueTTM || 0)),
  },
  {
    label: "Stock-Based Compensation to Revenue",
    subTitle: "Employee stock rewards relative to revenue.",
    render: (company: CompanyKeyMetrics) =>
      formatRatio(Number(company.stockBasedCompensationToRevenueTTM || 0)),
  },
  {
    label: "Symbol",
    subTitle: "Company ticker symbol.",
    render: (company: CompanyKeyMetrics) => company.symbol,
  },
  {
    label: "Tangible Asset Value",
    subTitle: "Value of physical assets like property and equipment.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.tangibleAssetValueTTM || 0)),
  },
  {
    label: "Tax Burden",
    subTitle: "Proportion of earnings retained after tax.",
    render: (company: CompanyKeyMetrics) => formatRatio(Number(company.taxBurdenTTM || 0)),
  },
  {
    label: "Working Capital",
    subTitle: "Difference between current assets and current liabilities.",
    render: (company: CompanyKeyMetrics) =>
      formatLargeMonetaryNumber(Number(company.workingCapitalTTM || 0)),
  },
];

const CompanyProfile = (props: Props) => {
  const ticker = useOutletContext<string>();
  const [companyData, setCompanyData] = useState<CompanyKeyMetrics>();
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const getCompanyKeyMetrics = async () => {
      try {
        const value = await getKeyMetrics(ticker);
        setCompanyData(value?.data[0]);
      } catch (err: any) {
        if (err.response?.data) {
          const detailedMessage =
            typeof err.response.data === "string"
              ? err.response.data
              : err.response.data?.error || JSON.stringify(err.response.data);
          setErrorDetails(
            detailedMessage || "Something went wrong while fetching company key metrics."
          );
        }
        setError(err.message || "Something went wrong while fetching data.");
      } finally {
        setLoading(false);
      }
    };
    getCompanyKeyMetrics();
  }, []);

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 font-medium">
        ⚠️ Error fetching data: {error} - {errorDetails}
      </div>
    );
  }

  return (
    <>
      {companyData ? (
        <>
          <RatioList data={companyData} config={tableConfig} />
          <div className="pt-4 w-full md:max-w-[25vw]">
            <StockComment stockSymbol={ticker} />
          </div>
        </>
      ) : (
        <div className="text-gray-500">No data available.</div>
      )}
    </>
  );
};

export default CompanyProfile;
